import {
  Building2,
  ChartColumn,
  ChevronDown,
  KanbanSquare,
  ListChecks,
  LockKeyhole,
  KeyRound,
  LogOut,
  MessageSquareText,
  MoreHorizontal,
  Plug,
  ScrollText,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthProvider';
import { CompanyBrainButton } from '../features/brain/CompanyBrainButton';

type NavigationItem = Readonly<{
  to: string;
  label: string;
  icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
}>;

const CORE_NAVIGATION: readonly NavigationItem[] = [
  { to: '/pipeline', label: 'Воронка', icon: KanbanSquare },
  { to: '/companies', label: 'Компании', icon: Building2 },
  { to: '/tasks', label: 'Задачи', icon: ListChecks },
  { to: '/reports', label: 'Аналитика', icon: ChartColumn },
];

const SECONDARY_NAVIGATION: readonly NavigationItem[] = [
  { to: '/agent', label: 'Агент', icon: MessageSquareText },
  { to: '/integrations', label: 'Интеграции', icon: Plug },
];

const ADMIN_NAVIGATION: readonly NavigationItem[] = [
  { to: '/settings/users', label: 'Пользователи', icon: Users },
  { to: '/settings/access', label: 'Доступы', icon: LockKeyhole },
  { to: '/settings/audit', label: 'Журнал', icon: ScrollText },
];

function NavigationLink({ item, demo }: { item: NavigationItem; demo: boolean }): ReactNode {
  const Icon = item.icon;
  return (
    <NavLink
      to={`${item.to}${demo ? '?demo=1' : ''}`}
      className={({ isActive }) => `global-nav-link${isActive ? ' global-nav-link-active' : ''}`}
    >
      <Icon size={20} aria-hidden />
      <span className="global-nav-label">{item.label}</span>
    </NavLink>
  );
}

export function AppShell(): ReactNode {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const demo = new URLSearchParams(location.search).get('demo') === '1';
  const actor = auth.session?.actor;
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const initial = actor?.name.trim().charAt(0).toLocaleUpperCase('ru') || 'Э';

  useEffect(() => {
    setMobileMoreOpen(false);
  }, [location.pathname]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-workspace">Перейти к содержимому</a>
      <aside className="global-rail" aria-label="Основная навигация">
        <NavLink className="brand-mark" to={`/pipeline${demo ? '?demo=1' : ''}`} aria-label="Echo CRM">
          <span className="brand-symbol" aria-hidden>e</span>
          <span className="brand-name">Echo CRM</span>
        </NavLink>

        <nav className="global-navigation">
          <div className="global-navigation-primary">
            {CORE_NAVIGATION.map((item) => <NavigationLink item={item} demo={demo} key={item.to} />)}
            <div className="desktop-navigation-secondary">
              {SECONDARY_NAVIGATION.map((item) => <NavigationLink item={item} demo={demo} key={item.to} />)}
              <CompanyBrainButton variant="rail" />
            </div>
            <button
              className={`global-nav-link mobile-more-trigger${mobileMoreOpen ? ' global-nav-link-active' : ''}`}
              type="button"
              aria-label={mobileMoreOpen ? 'Закрыть дополнительное меню' : 'Открыть дополнительное меню'}
              aria-expanded={mobileMoreOpen}
              onClick={() => setMobileMoreOpen((current) => !current)}
            >
              <MoreHorizontal size={20} aria-hidden />
              <span className="global-nav-label">Ещё</span>
            </button>
          </div>

          {actor?.role === 'admin' ? (
            <div className="global-navigation-admin" aria-label="Администрирование">
              {ADMIN_NAVIGATION.map((item) => <NavigationLink item={item} demo={demo} key={item.to} />)}
            </div>
          ) : null}
        </nav>

        <div className="rail-footer">
          {!location.pathname.startsWith('/agent') ? (
            <button
              className="agent-launch-button"
              type="button"
              onClick={() => navigate(`/agent${demo ? '?demo=1' : ''}`)}
            >
              <MessageSquareText size={18} aria-hidden />
              <span>Запустить Echo агент</span>
            </button>
          ) : null}
          <button
            className="profile-control"
            type="button"
            aria-label="Открыть меню профиля"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((current) => !current)}
          >
            <span className="profile-avatar" aria-hidden>{initial}</span>
            <span className="profile-copy">
              <strong>{actor?.name}</strong>
              <small>{actor?.role === 'admin' ? 'Администратор' : actor?.role === 'manager' ? 'Менеджер' : 'Наблюдатель'}</small>
            </span>
            <ChevronDown size={16} aria-hidden />
          </button>
          {profileOpen ? (
            <div className="profile-menu" role="menu">
              <div className="profile-menu-identity"><strong>{actor?.name}</strong><span>{actor?.email}</span></div>
              <button type="button" role="menuitem" onClick={() => { setProfileOpen(false); navigate('/change-password'); }}>
                <KeyRound size={16} aria-hidden />
                Сменить пароль
              </button>
              <button type="button" role="menuitem" onClick={() => void auth.signOut()}>
                <LogOut size={16} aria-hidden />
                Выйти из CRM
              </button>
            </div>
          ) : null}
        </div>
      </aside>

      {mobileMoreOpen ? (
        <div className="mobile-more-backdrop" role="presentation" onClick={() => setMobileMoreOpen(false)}>
          <section
            className="mobile-more-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Дополнительное меню"
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <strong>Ещё</strong>
              <button type="button" aria-label="Закрыть дополнительное меню" onClick={() => setMobileMoreOpen(false)}>
                <X size={20} aria-hidden />
              </button>
            </header>
            <nav>
              {SECONDARY_NAVIGATION.map((item) => <NavigationLink item={item} demo={demo} key={item.to} />)}
              <CompanyBrainButton variant="rail" />
            </nav>
          </section>
        </div>
      ) : null}

      <section className="app-workspace" id="main-workspace">
        <Outlet />
      </section>
    </div>
  );
}
